import React from 'react';
import { pathOr } from 'ramda';
import { Panel } from 'react-bootstrap';
import Table from '../Table';
import { getServiceAuthenticationAttributes, getServiceGroupsOrUsers } from '../../sources/service';
import {
    changeFlowGroupAttribute,
    deleteFlowGroup,
    changeFlowUserAttribute,
    deleteFlowUser,
} from '../../actions/reduxActions/flowConfigurations';
import { addNotification } from '../../actions/reduxActions/notification';
import { connect } from 'react-redux';

import UserGroupSearch from './properties/UserGroupSearch';

const initialState = {
    // This is all group or user atrributes from the Draw 2 service
    // Used to get all of the group or user attributes the service supports
    isLoadingServiceGroupOrUserAttributeData: true,
    serviceGroupOrUserAttributeData: [],
    // Used to indicate we encountered an error trying to receive groups or users
    errorLoadingGroupsOrUsers: false,
    errorMessage: null,
    isPrefetchingServiceData: true,
};

export class GroupOrUserSection extends React.Component {
    constructor(props) {
        super(props);

        this.state = initialState;

        this.groupOrUser = {
            // For printing out to the User in lowercase: "group" or "user"
            label: this.props.textGroupOrUser,
            // For printing out to the User in sentence case: "Group" or "User"
            capitalLabel: this.props.textGroupOrUser.charAt(0).toUpperCase() + this.props.textGroupOrUser.slice(1),
            // For changing the field on flowData.authorization: "groups" or "users"
            flowDataField: `${this.props.textGroupOrUser}s`,
            // For checking in code if the Section is for groups or users: "group" or "user"
            id: this.props.textGroupOrUser,
        };
    }

    componentDidMount() {
        this.loadServiceGroupsOrUsersAttributes(this.props.serviceElementId);
        this.prefetch();
    }

    componentDidUpdate(prevProps) {
        if (this.props.serviceElementId !== prevProps.serviceElementId) {
            this.state = initialState;
            this.loadServiceGroupsOrUsersAttributes(this.props.serviceElementId);
            this.prefetch();
        }
    }

    /**
     * We need to prefetch users/groups so that we can determine the human-readable name to be displayed.
     * We only save the user/group ID to the engine and not the human-readable name as this might change
     * on the IDP side.
     */
    prefetch = async () => {
        const authorizedUsersOrGroups = pathOr([], ['props', 'authorizationElement', 'authorization', this.groupOrUser.flowDataField], this);

        /**
         * Contruct objectData to send in the service request. This is so that we only prefetch the users/groups
         * that are already authorized to access the Flow.
         */
        const objectData = authorizedUsersOrGroups.map(aug => ({
            developerName: this.groupOrUser.id === 'group' ? 'GroupAuthorizationGroup' : 'GroupAuthorizationUser',
            properties: [
                { developerName: 'AuthenticationId', contentType: 'ContentString', contentValue: aug.authenticationId },
            ],
        }));

        if (objectData.length > 0) {
            try {
                
                const serviceUserResults = await getServiceGroupsOrUsers({
                    token: this.props.authenticationToken,
                    tenantId: this.props.tenantId,
                    serviceId: this.props.serviceElementId,
                    type: this.groupOrUser.id,
                    search: null,
                    objectData,
                });
    
                const matchingResults = authorizedUsersOrGroups.map((f) => {
                    const match = serviceUserResults.objectData.find(s => s.externalId === f.authenticationId);
                    if (match) {
                        const friendlyNameProperty = match.properties.find(
                            property => property.developerName === 'FriendlyName',
                        );
            
                        const contentOrEmptyString = pathOr('', ['contentValue']);
                        const friendlyName = contentOrEmptyString(friendlyNameProperty);
                        return { ...f, friendlyName };
                    }
    
                    return f;
                });
    
                this.props.setUsersOrGroups(this.props.elementId, matchingResults, this.groupOrUser.id);
                this.setState({ isPrefetchingServiceData: false });
    
            } catch ({ message }) {
                this.props.addNotification({
                    type: 'error',
                    message,
                    isPersistent: true,
                });
    
                this.setState({ isPrefetchingServiceData: false });
            }
            
        } else {
            this.setState({ isPrefetchingServiceData: false });
        }
    }

    formatErrorMessage(error) {
        const err = JSON.parse(error.message);
        return `${(err.kind).toUpperCase()} ERROR:
            ${err.statusCode} ${err.message}\n
            ${err.statusCode === 404 ? err.uri : null}`;
    }

    loadServiceGroupsOrUsersAttributes(serviceElementId) {
        if (!serviceElementId) {
            // There is nothing to load for this service
            this.setState({
                isLoadingServiceGroupOrUserAttributeData: false,
            });
            return;
        }

        this.setState({
            isLoadingServiceGroupOrUserAttributeData: true,
            errorLoadingGroupsOrUsers: false,
            errorMessage: null,
        });

        // Get Service GroupOrUser attributes
        getServiceAuthenticationAttributes({
            token: this.props.authenticationToken,
            tenantId: this.props.tenantId,
            serviceId: serviceElementId,
            type: this.groupOrUser.id,
        }).then(
            (groupOrUserAttributeData) => {
                this.setState({
                    serviceGroupOrUserAttributeData: pathOr([], ['outputs', '0', 'objectData'], groupOrUserAttributeData),
                    isLoadingServiceGroupOrUserAttributeData: false,
                });
            },
        ).catch(
            (error) => {
                this.setState({
                    errorLoadingGroupsOrUsers: true,
                    errorMessage: error.message,
                    serviceGroupOrUserAttributeData: [],
                    isLoadingServiceGroupOrUserAttributeData: false,
                });
                this.props.addNotification({
                    type: 'error',
                    message: this.formatErrorMessage(error),
                    isPersistent: false,
                });
            },
        );
    }

    deleteFlowGroupOrUser(object) {
        if (this.groupOrUser.id === 'group') {
            this.props.deleteGroup(object);
        } else {
            this.props.deleteUser(object);
        }
    }

    changeFlowGroupOrUserAttribute(object) {
        if (this.groupOrUser.id === 'group') {
            this.props.updateGroupAttribute(object);
        } else {
            this.props.updateUserAttribute(object);
        }
    }

    render() {

        // If we are still loading the group or user area
        if (
            this.state.isLoadingServiceGroupOrUserAttributeData ||
            this.state.isPrefetchingServiceData
        ) {
            // Show a loading spinner
            return (
                <div className="wait-container-wrapper">
                    {React.createElement(
                        manywho.component.getByName('wait'),
                        { isVisible: true, message: `Loading ${this.groupOrUser.label}s` },
                        null,
                    )}
                </div>
            );
        }

        // If an error was encountered loading groups or users
        if (this.state.errorLoadingGroupsOrUsers) {
            return (
                <Panel className="collapsible-error-panel" bsStyle="danger">
                    <Panel.Heading>
                        <Panel.Title>
                            {
                                `Encountered an error when contacting the Service
                                for ${this.groupOrUser.capitalLabel} restrictions!
                                Please ensure the service is accessible!`
                            }
                        </Panel.Title>
                        <Panel.Toggle componentClass="a" className="panel-toggle" />
                    </Panel.Heading>
                    <Panel.Collapse>
                        <Panel.Body>
                            <pre>
                                {
                                    `${this.state.errorMessage}`
                                }
                            </pre>
                        </Panel.Body>
                    </Panel.Collapse>
                </Panel>
            );
        }

        // If the service doesn't support groups or users
        if (
            manywho.utils.isNullOrUndefined(this.props.serviceElementId) ||
            pathOr(0, ['state', 'serviceGroupOrUserAttributeData', 'length'], this) === 0
        ) {
            return (
                <span className="help-block">
                    {`${this.groupOrUser.capitalLabel} restrictions are not supported by this Service`}
                </span>
            );
        }

        let FullDetailGroupOrUserList = [];
        // If the Service has groups or users AND the Flow has groups or users
        if (
            pathOr(0, ['props', 'authorizationElement', 'authorization', this.groupOrUser.flowDataField, 'length'], this) > 0
        ) {
            // Load the groups or users fully
            // For each group or user that the Flow has
            FullDetailGroupOrUserList = pathOr([], ['props', 'authorizationElement', 'authorization', this.groupOrUser.flowDataField], this).map(
                (groupOrUserInFlow, index) => ({
                    // id used by select box to know which is value
                    id: groupOrUserInFlow.authenticationId,
                    attribute: groupOrUserInFlow.attribute,
                    friendlyName: groupOrUserInFlow.friendlyName,
                    developerSummary: groupOrUserInFlow.developerSummary,
                    // Used to know which group or user is being edited
                    index,
                }),
            );
        }
        return (
            <React.Fragment>
                <h2>
                    {`${this.groupOrUser.capitalLabel}s`}
                </h2>
                <UserGroupSearch
                    serviceId={this.props.serviceElementId}
                    tenantId={this.props.tenantId}
                    token={this.props.authenticationToken}
                    elementId={this.props.elementId}
                    authorizedUserOrGroups={FullDetailGroupOrUserList}
                    type={this.groupOrUser.id}
                    addUsersOrGroups={this.props.addUsersOrGroups}
                />
                <div className="search-wrapper">
                    <Table
                        className="table scroll-table-entry margin-top"
                        columns={
                            [
                                {
                                    renderHeader: () => 'Actions',
                                    renderCell: ({ item }) => (
                                        <div>
                                            <button
                                                className="btn-icon danger"
                                                title={`Remove ${this.groupOrUser.capitalLabel}`}
                                                onClick={() => this.deleteFlowGroupOrUser({
                                                    elementId: this.props.elementId,
                                                    index: item.index,
                                                })}
                                            >
                                                <span className="glyphicon glyphicon-trash" />
                                            </button>
                                        </div>),
                                    size: '50px',
                                },
                                {
                                    renderHeader: () => 'Name',
                                    renderCell: ({ item }) => ( // Uses information from item.id
                                        <>{item.friendlyName || item.id}</>
                                    ),
                                    size: '200px',
                                },
                                {
                                    renderHeader: () => 'Attribute',
                                    renderCell: ({ item }) => ( // Uses information from item.attribute
                                        <select
                                            onChange={({ target }) => this.changeFlowGroupOrUserAttribute({
                                                elementId: this.props.elementId,
                                                groupOrUserAttributeId: target.value,
                                                index: item.index,
                                            })}
                                            className="form-select"
                                            defaultValue={item.attribute}
                                        >
                                            {
                                                item.attribute === -1 ? <option key="-1" value="">--Please Select--</option> : null
                                            }
                                            {
                                                this.state.serviceGroupOrUserAttributeData.map(groupOrUserAttributeOption => (
                                                    <option
                                                        key={groupOrUserAttributeOption.externalId}
                                                        value={groupOrUserAttributeOption.externalId}
                                                    >
                                                        {groupOrUserAttributeOption.properties.find(
                                                            property => property.developerName === 'Label',
                                                        ).contentValue}
                                                    </option>
                                                ))
                                            }
                                        </select>),
                                    size: '200px',
                                },
                            ]
                        }
                        items={FullDetailGroupOrUserList}
                        keyGenerator={(item, index) => item.id + item.attribute + index}
                    />
                </div>
            </React.Fragment>
        );
    }
}

const mapDispatchToProps = {
    changeFlowGroupAttribute,
    deleteFlowGroup,
    changeFlowUserAttribute,
    deleteFlowUser,
    addNotification,
};

export default connect(
    null,
    mapDispatchToProps,
)(GroupOrUserSection);
